import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Settings, BarChart3, FileText, Menu, X, Users } from 'lucide-react';

interface AdminNavbarProps {
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
  };
  onLogout: () => void;
}

export function AdminNavbar({ user, onLogout }: AdminNavbarProps) {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState('/logo-gold-small.png');

  useEffect(() => {
    const updateLogo = () => {
      if (window.innerWidth <= 768) {
        // Mobile: recberry_gold_r.png
        setLogoSrc('/recberry_gold_r.png');
      } else {
        // Desktop/Tablet: logo-gold-small.png
        setLogoSrc('/logo-gold-small.png');
      }
    };

    updateLogo();
    window.addEventListener('resize', updateLogo);
    return () => window.removeEventListener('resize', updateLogo);
  }, []);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <style>{styles}</style>
      <nav id="navbar">
        <div className="navbar-container">
          <div className="logo">
            <Link to="/admin/dashboard">
              <img 
                src={logoSrc} 
                alt="recberry" 
                className="logo-image"
              />
            </Link>
          </div>
          
          {/* Hamburger Menu Button */}
          <button className="hamburger-btn" onClick={toggleMenu}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Desktop Menu */}
          <div className="menu desktop-menu">
            <Link 
              to="/admin/dashboard" 
              className={location.pathname === '/admin/dashboard' ? 'active' : ''}
            >
              <BarChart3 size={16} />
              DASHBOARD
            </Link>
            <Link 
              to="/admin/waitlist" 
              className={location.pathname === '/admin/waitlist' ? 'active' : ''}
            >
              <Users size={16} />
              WAITLIST
            </Link>
            <Link 
              to="/admin/invitations" 
              className={location.pathname === '/admin/invitations' ? 'active' : ''}
            >
              <Settings size={16} />
              INVITATIONS
            </Link>
            <Link 
              to="/admin/content" 
              className={location.pathname === '/admin/content' ? 'active' : ''}
            >
              <Settings size={16} />
              CONTENT
            </Link>
            <Link 
              to="/admin/progress" 
              className={location.pathname === '/admin/progress' ? 'active' : ''}
            >
              <BarChart3 size={16} />
              PROGRESS
            </Link>
            <Link 
              to="/admin/resources" 
              className={location.pathname === '/admin/resources' ? 'active' : ''}
            >
              <FileText size={16} />
              RESOURCES
            </Link>
          </div>
          
          {/* Desktop User Menu */}
          <div className="user-menu desktop-user-menu">
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={16} />
              LOGOUT
            </button>
          </div>
          
        </div>
      </nav>
      
      {/* Mobile Overlay */}
      <div className={`mobile-overlay ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu}></div>
      
      {/* Mobile Side Menu */}
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <Link 
          to="/admin/dashboard" 
          className={location.pathname === '/admin/dashboard' ? 'active' : ''}
          onClick={() => setIsMenuOpen(false)}
        >
          <BarChart3 size={16} />
          DASHBOARD
        </Link>
        <Link 
          to="/admin/waitlist" 
          className={location.pathname === '/admin/waitlist' ? 'active' : ''}
          onClick={() => setIsMenuOpen(false)}
        >
          <Users size={16} />
          WAITLIST
        </Link>
        <Link 
          to="/admin/invitations" 
          className={location.pathname === '/admin/invitations' ? 'active' : ''}
          onClick={() => setIsMenuOpen(false)}
        >
          <Settings size={16} />
          INVITATIONS
        </Link>
        <Link 
          to="/admin/content" 
          className={location.pathname === '/admin/content' ? 'active' : ''}
          onClick={() => setIsMenuOpen(false)}
        >
          <Settings size={16} />
          CONTENT
        </Link>
        <Link 
          to="/admin/progress" 
          className={location.pathname === '/admin/progress' ? 'active' : ''}
          onClick={() => setIsMenuOpen(false)}
        >
          <BarChart3 size={16} />
          PROGRESS
        </Link>
        <Link 
          to="/admin/resources" 
          className={location.pathname === '/admin/resources' ? 'active' : ''}
          onClick={() => setIsMenuOpen(false)}
        >
          <FileText size={16} />
          RESOURCES
        </Link>
        <button onClick={handleLogout} className="logout-btn mobile-logout">
          <LogOut size={16} />
          LOGOUT
        </button>
      </div>
    </>
  );
}

const styles = `
  #navbar {
    background: var(--dark);
    border-bottom: 1px solid var(--orange);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    width: 100%;
  }

  .navbar-container {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    height: 80px;
    width: 100%;
    position: relative;
  }

  .logo {
    display: flex;
    align-items: center;
  }

  .logo-image {
    height: 48px;
    width: auto;
  }

  .desktop-menu {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    justify-content: center;
  }

  .desktop-menu a {
    color: var(--white);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    white-space: nowrap;
    min-width: fit-content;
  }

  .desktop-menu a:hover {
    background: rgba(216, 183, 37, 0.1);
    color: var(--orange);
  }

  .desktop-menu a.active {
    background: var(--orange);
    color: var(--dark);
  }

  .user-menu {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }


  .logout-btn {
    background: transparent;
    border: 1px solid var(--orange);
    color: var(--orange);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;
  }

  .logout-btn:hover {
    background: var(--orange);
    color: var(--dark);
  }

  @media (max-width: 1024px) {
    .desktop-menu {
      gap: 0.75rem;
    }
    
    .desktop-menu a {
      font-size: 0.8rem;
      padding: 0.5rem 0.6rem;
    }
  }

  @media (max-width: 768px) {
    .navbar-container {
      padding: 0 15px;
    }
    
    .desktop-menu {
      gap: 0.5rem;
    }
    
    .desktop-menu a {
      font-size: 0.75rem;
      padding: 0.4rem 0.5rem;
    }
    
    
    .logo-image {
      height: 40px;
    }
  }

  /* Hamburger Menu Styles */
  .hamburger-btn {
    display: none;
    background: none;
    border: none;
    color: var(--orange);
    cursor: pointer;
    padding: 0.5rem;
    margin-left: auto;
    align-items: center;
    justify-content: center;
  }

  .desktop-menu {
    display: flex;
  }

  .desktop-user-menu {
    margin-left: auto;
  }

  .mobile-menu {
    position: fixed;
    top: 0;
    right: -300px;
    width: 300px;
    height: 100vh;
    background: var(--dark);
    flex-direction: column;
    padding: 2rem 1rem;
    gap: 1.5rem;
    transition: right 0.3s ease;
    z-index: 1001;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
    visibility: hidden;
  }

  .mobile-menu.open {
    right: 0;
    visibility: visible;
  }

  .mobile-menu a {
    color: var(--white);
    text-decoration: none;
    font-weight: 500;
    font-size: 1rem;
    padding: 1rem;
    transition: color 0.2s ease;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .mobile-menu a:hover {
    color: var(--orange);
  }

  .mobile-menu a.active {
    color: var(--orange);
  }

  .mobile-logout {
    background: var(--orange);
    color: var(--dark);
    border: none;
    padding: 1rem;
    border-radius: 4px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .mobile-logout:hover {
    background: #c4a020;
  }

  /* Overlay for mobile menu */
  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .mobile-overlay.open {
    opacity: 1;
    visibility: visible;
  }

  @media (max-width: 1024px) {
    .navbar-container {
      padding: 0 1rem;
    }
    
    .hamburger-btn {
      display: flex;
    }
    
    .desktop-menu,
    .desktop-user-menu {
      display: none;
    }
  }
`;
